import { useCallback, useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import {
  NBXStorage,
  getSecureStorageErrorCode,
  isAuthenticationCancelled,
  isStoredValueUnrecoverable
} from '@nbx/frontend-helpers/storage';
import { Session } from '@nbx/frontend-helpers/types';
import { logStep } from '@nbx/frontend-helpers/logger';
// eslint-disable-next-line import/no-cycle
import {
  readDeviceToken,
  isDeviceTokenExists,
  getLastBioAuthenticatedEpochMs,
  refreshSession,
  refreshUserToken,
  refreshAccountToken
} from '.';
import { useLastBioAuthenticatedEpochMs, clearGlobalReadDeviceTokenPromise } from './deviceToken';
import { useAuthAccountClaims, useAuthSession } from '@nbx/frontend-helpers/subscribe';
import { publishSessionExpired, clearGlobalFetchSessionTokenPromise } from './session';
// eslint-disable-next-line import/no-cycle
import { APIFetchError } from '@nbx/frontend-helpers/fetch-api';

const TIMEOUT_MS = 5 * 60 * 1000;

// The biometric prompt is paced by the user, so it gets a generous bound; the network legs get a
// tighter one. Both need *some* bound: nothing in the fetch layer is abortable, and every step
// below memoises its in-flight promise globally, so one hung call would leave the gate spinning
// forever and hand the same dead promise to every retry.
const BIOMETRIC_PROMPT_TIMEOUT_MS = 60 * 1000;
const TOKEN_REFRESH_TIMEOUT_MS = 30 * 1000;

class BiometricGateTimeoutError extends Error {
  constructor(stage: string) {
    super(`useBiometricGate timed out while ${stage}`);
    this.name = 'BiometricGateTimeoutError';
    Object.setPrototypeOf(this, BiometricGateTimeoutError.prototype);
  }
}

/**
 * Rejects if `work` has not settled within `ms`. `discardCachedPromise` drops the module/window
 * level cache of the timed-out call so the next attempt starts a fresh one instead of awaiting
 * the promise that already hung.
 */
const withTimeout = <T>(
  work: Promise<T>,
  ms: number,
  stage: string,
  discardCachedPromise: () => void
): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const guard = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => {
      discardCachedPromise();
      reject(new BiometricGateTimeoutError(stage));
    }, ms);
  });
  return Promise.race([work, guard]).finally(() => clearTimeout(timer));
};

const discardCachedAccountTokenPromise = (): void => {
  window.fetchAccountTokenPromise = null;
  window.fetchAccountTokenPromiseId = null;
};

const discardCachedUserTokenPromise = (): void => {
  window.fetchUserTokenPromise = null;
};

const isTimeout = async () => {
  const lastAuthDate = await getLastBioAuthenticatedEpochMs();
  return !lastAuthDate || Date.now() - +lastAuthDate > TIMEOUT_MS;
};

const isBiometricAvailable = async () => Capacitor.isNativePlatform() && (await isDeviceTokenExists());

interface UseBiometricGateProps {
  showWhenNoSession: boolean;
  onSuccess?: (session: Session) => void;
  tag?: string;
}

export const useBiometricGate = (
  { showWhenNoSession = false, onSuccess, tag }: UseBiometricGateProps = { showWhenNoSession: false }
) => {
  const log = (...args: unknown[]): void => console.log(`useBiometricGate: [${tag}]`, ...args);
  const [error, setError] = useState(false);
  const [hardFailure, setHardFailure] = useState(false);
  const [loading, setLoading] = useState(false);
  const authSession = useAuthSession();
  const lastBioAuthenticatedEpochMs = useLastBioAuthenticatedEpochMs();

  const accountClaims = useAuthAccountClaims();
  const isTestAccount = accountClaims?.roles?.includes('test');

  const [show, setShow] = useState<boolean>(false);
  const [ask, setAsk] = useState<boolean>(false);

  const refreshTokens = useCallback(async (session?: Session | null): Promise<boolean> => {
    if (!session) {
      log(`cannot refreshTokens - no session`);
      return false;
    }
    try {
      setLoading(true);
      const activeAccountId = await NBXStorage.getItem('active-account');
      log(
        `refreshAccountToken(session.exp=${session?.exp}, session.accountId=${
          activeAccountId ?? session?.accountId
        })`
      );
      await withTimeout(
        refreshAccountToken(session, activeAccountId ?? session.accountId),
        TOKEN_REFRESH_TIMEOUT_MS,
        'refreshing the account token',
        discardCachedAccountTokenPromise
      );
      log(`refreshUserToken(session.exp=${session?.exp})`);
      await withTimeout(
        refreshUserToken(session),
        TOKEN_REFRESH_TIMEOUT_MS,
        'refreshing the user token',
        discardCachedUserTokenPromise
      );
      log(`refresh access tokens - DONE`);
      logStep('biometric', 'tokens_refreshed', { tag });
      setAsk(true);
      return true;
    } catch (e) {
      logStep('biometric', 'token_refresh_failed', {
        tag,
        timedOut: e instanceof BiometricGateTimeoutError
      });
      console.error(
        `useBiometricGate: cannot refresh tokens ${e instanceof Error ? e.message : 'unknown error'}`,
        e
      );
      // Reported through the return value, not through `error`. The frame calls this as a
      // pre-flight before the prompt, where a failure is expected after the tokens expire and is
      // about to be repaired by authenticate() minting a fresh session - flagging it there shows
      // the user an error for something that self-heals. Consumers that cannot recover decide to
      // surface it themselves.
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (show && authSession) {
      log(`refreshTokens(authSession.exp=${authSession?.exp})`);
      refreshTokens(authSession);
    } else if (show && authSession === null) {
      publishSessionExpired();
    }
  }, [show, authSession]);

  const authenticate = useCallback(async () => {
    try {
      setError(false);
      setHardFailure(false);
      setLoading(true);
      log(`readDeviceToken`);
      logStep('biometric', 'prompt_shown', { tag });
      const deviceTokenJSON = await withTimeout(
        readDeviceToken(),
        BIOMETRIC_PROMPT_TIMEOUT_MS,
        'reading the device token',
        clearGlobalReadDeviceTokenPromise
      );
      log(
        `readDeviceToken - refreshSession from (deviceTokenJSON=${!!deviceTokenJSON}), (isTestAccount=${isTestAccount})`
      );
      const session = await withTimeout(
        refreshSession(null, deviceTokenJSON, isTestAccount),
        TOKEN_REFRESH_TIMEOUT_MS,
        'refreshing the session',
        clearGlobalFetchSessionTokenPromise
      );
      log(`readDeviceToken - refreshSession done (new session.exp=${session?.exp})`);
      logStep('biometric', 'authenticated', { tag });
      onSuccess && onSuccess(session);
      setShow(false);
    } catch (e) {
      if (isAuthenticationCancelled(e)) {
        // A deliberate dismissal is not a failure. Leaving `error` false keeps the gate on its
        // plain "Authenticate" state instead of accusing the user of something going wrong.
        log(`prompt dismissed by the user - not surfacing an error`);
        logStep('biometric', 'dismissed', { tag });
        return;
      }
      if (e instanceof BiometricGateTimeoutError) {
        logStep('biometric', 'timed_out', { tag, message: e.message });
      }
      console.error(
        `useBiometricGate: authenticate error ${e instanceof Error ? e.message : 'unknown error'}`,
        e
      );
      setError(true);
      if (e instanceof APIFetchError && (e.response?.status === 401 || e.response?.status === 403)) {
        logStep('biometric', 'session_rejected', { tag, status: e.response?.status });
        setHardFailure(true);
      }
      // The device token cannot be read again on this device - the key was invalidated by a
      // lock-screen or enrollment change, or secure storage is gone. Retrying reads the same dead
      // value, so recover through credential login instead of offering a pointless retry.
      if (isStoredValueUnrecoverable(e)) {
        log(`device token is unrecoverable (${getSecureStorageErrorCode(e)}) - hard failure`);
        logStep('biometric', 'device_token_unrecoverable', {
          tag,
          code: getSecureStorageErrorCode(e)
        });
        setHardFailure(true);
      } else {
        logStep('biometric', 'failed', { tag, code: getSecureStorageErrorCode(e) });
      }
    } finally {
      log(`readDeviceToken - DONE`);
      setAsk(false);
      setLoading(false);
    }
  }, [isTestAccount]);

  useEffect(() => {
    (async () =>
      (!!showWhenNoSession || !!authSession) && (await isBiometricAvailable()) && (await isTimeout()))().then(
      setShow
    );
  }, [lastBioAuthenticatedEpochMs, showWhenNoSession, authSession]);

  const updateShow = useCallback(async () => {
    const show =
      (!!showWhenNoSession || !!(await NBXStorage.getItem('session'))) &&
      (await isBiometricAvailable()) &&
      (await isTimeout());
    log(`applicationDidBecomeActive updateShow (show=${show})`);
    setShow(show);
  }, [showWhenNoSession]);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      window.addEventListener('applicationDidBecomeActive', updateShow);
      return () => {
        window.removeEventListener('applicationDidBecomeActive', updateShow);
      };
    }
  }, [updateShow]);

  return { show, ask, authenticate, refreshTokens, loading, error, hardFailure };
};
